import Link from 'next/link';
import { FC, ReactNode } from 'react';
import { IoArrowForward, IoConstructOutline, IoCubeOutline, IoPersonCircleOutline } from 'react-icons/io5';

import { Box, Button, Group, RingProgress, Text } from '@mantine/core';

import { CompanyProfile, PermissionCompany } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';

import { ProgressChecklist } from 'components/company-profile/components/ProgressChecklist';
import { useProfilePermission } from 'components/company-profile/hooks/use-profile-permission';
import { useProfileProgress } from 'components/company-profile/hooks/use-profile-progress';

import cx from './IncompleteProfile.module.scss';

const IncompleteProfile: FC<{ company: CompanyProfile; isEmpty: boolean }> = ({ company, isEmpty }) => {
    const canEdit = useProfilePermission(PermissionCompany.EDIT);
    const { percentage, isLoading } = useProfileProgress(company);

    if (canEdit) {
        if (isLoading || percentage === null) return null;

        // Up to the "Incomplete" progress level (50%), the ring on the edit button alone is easy to miss
        const showCard = isEmpty || percentage <= 50;

        return showCard && <ProfileCompletenessCard company={company} withAction />;
    }

    if (!isEmpty) return null;

    return (
        <Box className={cx.root}>
            <Box className={cx.content}>
                <Text className={cx.eyebrow}>Profile in progress</Text>
                <Text className={cx.title}>{company.name} hasn't completed its profile yet</Text>
                <Text className={cx.description}>
                    Products, services and team details will show up here once they are added. In the meantime, explore
                    other companies.
                </Text>

                <Group gap="xs" className={cx.actions}>
                    <Button
                        component={Link}
                        href={CompanyHelpers.urls.overview()}
                        color="dark"
                        rightSection={<IoArrowForward />}
                    >
                        Browse companies
                    </Button>
                </Group>
            </Box>

            <SectionPreview />
        </Box>
    );
};

// Vertical fits a sidebar, with the ring on top
const ProfileCompletenessCard: FC<{
    company: CompanyProfile;
    vertical?: boolean;
    withAction?: boolean;
    className?: string;
}> = ({ company, vertical, withAction, className }) => {
    const { percentage, color } = useProfileProgress(company);

    const ring = (size: number, thickness: number) => (
        <RingProgress
            size={size}
            thickness={thickness}
            roundCaps
            sections={[{ value: percentage ?? 0, color: color ?? 'dark' }]}
            label={<Text className={cx.percentage}>{percentage}%</Text>}
            className={cx.ring}
        />
    );

    return (
        <Box className={`${cx.root} ${className ?? ''}`} data-vertical={vertical || undefined}>
            <Box className={cx.content}>
                <Group gap="md" wrap="nowrap" align="center">
                    {!vertical && ring(84, 7)}
                    <Box>
                        <Text className={cx.eyebrow}>Your profile is {percentage}% complete</Text>
                        <Text className={cx.title}>
                            {percentage === 100 ? 'Your profile is complete' : 'Complete your profile'}
                        </Text>
                    </Box>
                </Group>
                <Text className={cx.description}>
                    Fill in the steps below so visitors know what {company.name} offers.
                </Text>

                <ProgressChecklist company={company} className={cx.checklist} />

                {withAction && (
                    <Group gap="xs" className={cx.actions}>
                        <Button
                            component={Link}
                            href={CompanyHelpers.urls.edit(company.slug)}
                            color="dark"
                            rightSection={<IoArrowForward />}
                        >
                            Complete profile
                        </Button>
                    </Group>
                )}
            </Box>

            {vertical ? (
                <Box className={cx.preview} aria-hidden>
                    {ring(140, 10)}
                </Box>
            ) : (
                <SectionPreview />
            )}
        </Box>
    );
};

const SectionPreview = () => (
    <Box className={cx.preview} data-ghosts aria-hidden>
        <GhostSection icon={<IoCubeOutline />} label="Products" />
        <GhostSection icon={<IoConstructOutline />} label="Services" />
        <GhostSection icon={<IoPersonCircleOutline />} label="Team" />
    </Box>
);

const GhostSection: FC<{ icon: ReactNode; label: string }> = ({ icon, label }) => (
    <Box className={cx.ghost}>
        <Box className={cx.ghostIcon}>{icon}</Box>
        <Box className={cx.ghostText}>
            <Text className={cx.ghostLabel}>{label}</Text>
            <Box className={cx.ghostBar} />
            <Box className={cx.ghostBar} data-short />
        </Box>
    </Box>
);

export { IncompleteProfile, ProfileCompletenessCard };
